import React from 'react';
import { ListRenderItem, ListRenderItemInfo, StyleSheet, View } from 'react-native';

import { UserAvatar } from '@components/Avatar';
import { BigTextInputButton } from '@components/Button';
import Chip from '@components/Chip';
import FlatList from '@components/FlatList';
import Image from '@components/Image';
import ImagePicker from '@components/ImagePicker';
import { GroupPickerModal, PersonPickerModal } from '@components/Picker';
import Switch from '@components/Switch';
import { Subheading } from '@components/Typography';
import { useAppTheme } from '@context/ThemeContext';
import { logError } from '@helpers/Loggers';
import { Group, Person, Photo } from '@types';

import { SelectedImagePreview } from './SelectedImagePreview';
import { useFieldContext } from './context';

const AVATAR_SIZE = 25;
const PHOTO_INPUT_SIZE = 100;

export type PhotoListFieldProps = {
  /** Test id forwarded to the image picker control. */
  testID?: string;
};

export type PhotoFieldProps = {
  /** Renders the selected image and empty image picker as a circle. */
  circle?: boolean;
  /** Value written to the form when the selected photo is removed. */
  emptyValue?: null | undefined;
  /** Style applied to the selected image and empty image picker. */
  imageStyle?: React.ComponentProps<typeof Image>['style'];
  /** Style applied to the wrapper around the selected image. */
  containerStyle?: React.ComponentProps<typeof View>['style'];
  /** Test id forwarded to the image picker control. */
  testID?: string;
};

export function FormPhotoInput({
  circle,
  emptyValue,
  imageStyle,
  containerStyle,
  testID,
}: PhotoFieldProps) {
  const field = useFieldContext<Photo | null | undefined>();
  const appTheme = useAppTheme();
  const defaultedImageStyle = [
    styles.singleImage,
    { borderRadius: appTheme.roundness },
    imageStyle,
  ];

  return (
    <>
      {field.state.value ? (
        <SelectedImagePreview
          source={field.state.value}
          circle={circle}
          imageStyle={defaultedImageStyle}
          containerStyle={containerStyle}
          onRemove={() => field.handleChange(emptyValue)}
        />
      ) : (
        <View style={containerStyle}>
          <ImagePicker
            circle={circle}
            onSelected={field.handleChange}
            onError={(error) => logError(error)}
            style={defaultedImageStyle}
            testID={testID}
          />
        </View>
      )}
    </>
  );
}

export function FormPhotoListInput({ testID }: PhotoListFieldProps) {
  const field = useFieldContext<Photo[]>();
  const appTheme = useAppTheme();
  const photos = field.state.value ?? [];
  const imageRadiusStyle = { borderRadius: appTheme.roundness };

  const photosKeyExtractor = (item: Photo) => item.uri;

  const renderSelectedImage = ({ item }: ListRenderItemInfo<Photo>) => (
    <SelectedImagePreview
      source={{ uri: item.uri }}
      imageStyle={[styles.listImage, imageRadiusStyle]}
      containerStyle={styles.selectedImageContainer}
      onRemove={() => field.handleChange(photos.filter((photo) => photo.uri !== item.uri))}
    />
  );

  return (
    <FlatList
      data={photos}
      renderItem={renderSelectedImage}
      keyExtractor={photosKeyExtractor}
      horizontal
      ListFooterComponent={
        <ImagePicker
          onSelected={(photo) => field.handleChange([...photos, photo])}
          style={[styles.listImagePicker, imageRadiusStyle]}
          onError={(error) => logError(error)}
          testID={testID}
        />
      }
      contentContainerStyle={styles.listContentContainer}
    />
  );
}

export type GroupPickerFieldProps = React.ComponentProps<typeof View> & {
  /** Groups available to select. */
  groups: Group[];
  /** Label displayed on the picker button. */
  buttonText?: string;
  /** Test id forwarded to the picker button. */
  testID?: string;
};

export function FormGroupPickerInput({
  groups,
  buttonText = 'Share With Groups',
  testID,
  ...props
}: GroupPickerFieldProps) {
  const field = useFieldContext<Set<string>>();
  const [groupPickerVisible, setGroupPickerVisible] = React.useState(false);
  const selectedGroupIds = field.state.value ?? new Set<string>();

  const renderGroupChip: ListRenderItem<Group> = ({ item }) => (
    <Chip
      avatar={
        <UserAvatar displayName={item.displayName} photoUrl={item.photoUrl} size={AVATAR_SIZE} />
      }
      onClose={() =>
        field.handleChange(
          new Set([...selectedGroupIds].filter((groupId) => groupId !== item.groupId))
        )
      }
      style={styles.chip}>
      {item.displayName}
    </Chip>
  );

  const groupKeyExtractor = (item: Group) => item.groupId;

  return (
    <View>
      <FlatList
        data={groups.filter((group) => selectedGroupIds.has(group.groupId))}
        renderItem={renderGroupChip}
        keyExtractor={groupKeyExtractor}
        contentContainerStyle={styles.listContentContainer}
        horizontal
      />
      <View {...props}>
        <BigTextInputButton
          mode="outlined"
          icon="account-multiple"
          onPress={() => setGroupPickerVisible(!groupPickerVisible)}
          testID={testID}>
          {buttonText}
        </BigTextInputButton>
      </View>
      <GroupPickerModal
        groups={groups}
        selectedGroupIds={selectedGroupIds}
        setSelectedGroupIds={field.handleChange}
        visible={groupPickerVisible}
        onDismiss={() => setGroupPickerVisible(false)}
      />
    </View>
  );
}

export type PersonPickerFieldProps = React.ComponentProps<typeof View> & {
  /** People available to select. */
  people: Person[];
  /** Controls the optional "looking for people" switch. */
  joinsRequested?: boolean;
  /** Updates the optional "looking for people" switch. */
  onJoinsRequestedChange?: (joinsRequested: boolean) => void;
  /** Label displayed on the picker button. */
  buttonText?: string;
  /** Test id forwarded to the picker button. */
  testID?: string;
};

export function FormPersonPickerInput({
  people,
  joinsRequested,
  onJoinsRequestedChange,
  buttonText = 'Invite People',
  testID,
  ...props
}: PersonPickerFieldProps) {
  const field = useFieldContext<Set<string>>();
  const [peopleSearchVisible, setPeopleSearchVisible] = React.useState(false);
  const selectedPeopleIds = field.state.value ?? new Set<string>();
  const showJoinsRequested = joinsRequested !== undefined && onJoinsRequestedChange !== undefined;

  const renderPersonChip: ListRenderItem<Person> = ({ item }) => (
    <Chip
      avatar={
        <UserAvatar displayName={item.displayName} photoUrl={item.photoUrl} size={AVATAR_SIZE} />
      }
      onClose={() =>
        field.handleChange(
          new Set([...selectedPeopleIds].filter((personId) => personId !== item.personId))
        )
      }
      style={styles.chip}>
      {item.displayName}
    </Chip>
  );

  const peopleKeyExtractor = (item: Person) => item.personId;

  return (
    <View>
      <FlatList
        data={people.filter((person) => selectedPeopleIds.has(person.personId))}
        renderItem={renderPersonChip}
        keyExtractor={peopleKeyExtractor}
        contentContainerStyle={styles.listContentContainer}
        horizontal
      />
      <View {...props}>
        <BigTextInputButton
          mode="outlined"
          icon="account"
          onPress={() => setPeopleSearchVisible(!peopleSearchVisible)}
          testID={testID}>
          {buttonText}
        </BigTextInputButton>
        {showJoinsRequested ? (
          <View style={styles.row}>
            <Subheading style={styles.joinsRequestedText}>Looking for people to join?</Subheading>
            <Switch
              style={styles.switch}
              value={joinsRequested}
              onValueChange={() => onJoinsRequestedChange(!joinsRequested)}
            />
          </View>
        ) : null}
      </View>
      <PersonPickerModal
        people={people}
        selectedPeople={selectedPeopleIds}
        setSelectedPeople={field.handleChange}
        visible={peopleSearchVisible}
        onDismiss={() => setPeopleSearchVisible(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  chip: {
    marginRight: 2,
  },
  joinsRequestedText: {
    textAlign: 'center',
  },
  listContentContainer: {
    paddingHorizontal: 20,
  },
  listImage: {
    height: PHOTO_INPUT_SIZE,
    width: PHOTO_INPUT_SIZE,
  },
  listImagePicker: {
    height: PHOTO_INPUT_SIZE,
    marginRight: 10,
    marginVertical: 15,
    width: PHOTO_INPUT_SIZE,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  selectedImageContainer: {
    marginRight: 10,
    marginVertical: 15,
  },
  singleImage: {
    height: PHOTO_INPUT_SIZE,
    width: PHOTO_INPUT_SIZE,
  },
  switch: {
    marginHorizontal: 5,
  },
});
